import { useState } from 'react'

import { Features } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import { CompensationsSettingsPageDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import ConnectedModules, {
  ModuleConfig,
} from 'modules/compensations/pages/settings/ConnectedModules'
import CompensationSettings from 'modules/compensations/pages/settings/Content/CompensationSettings'
import Integrations from 'modules/compensations/pages/settings/Content/Integrations'
import PayrollConceptsCard from 'modules/compensations/pages/settings/Content/PayrollConcepts'
import Cycles from 'modules/compensations/pages/settings/Cycles'
import { useCanUpsellPayroll } from 'modules/contracts/hooks/useCanUpsellPayroll'
import PrimoboxActivationRow from 'modules/integrations_primobox/components/ActivationRow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import EmployeeUpdates from 'modules/payroll_bundle/pages/settings/Content/EmployeeUpdates'
import ProfessionalSettings from 'modules/payroll_bundle/pages/settings/Content/ProfessionalSettings'

const featureAvailabilitiesIds: Features[] = [
  Features.compensations_management,
  Features.time_tracking_management,
  Features.expenses_reimburse_through_payroll,
  Features.benefits_core,
]

const CompensationsSettingsPage = () => {
  const [isPrimoboxEnabled, setPrimoboxEnabled] = useState(false)
  const { company } = useCurrentSession()
  const { data, error, loading } = useQuery(CompensationsSettingsPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [
        'factorial_professionals',
        'inhouse_payroll',
        'integrations_primobox_activable',
        'integrations_primobox_active',
        'dev_compensations_cycles_management',
      ],
      featureAvailabilitiesIds,
    },
  })
  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
    },
    false
  )

  const compensationSettings = data?.compensations.compensationSettingsConnection.nodes
  const canEditPayrollConcepts = data?.compensations._policies.payrollConceptsEdit.can ?? false

  const features = useUnsafeMemo(() => data?.features?.featuresConnection.nodes || [], [data])

  const hasFeature = useUnsafeCallback(
    (featureName: AllFeatureIds) =>
      features.find((feature) => feature.name === featureName)?.enabled || false,
    [features]
  )
  const featureAvailabilities = useUnsafeMemo(
    () => data?.companies?.featureAvailabilitiesConnection.nodes || [],
    [data]
  )

  const hasTimeTracking = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.time_tracking_management
  )
  const hasExpenses = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.expenses_reimburse_through_payroll
  )
  const hasCompensations = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )

  const hasBenefits = isFeatureAvailabilityEnabled(featureAvailabilities, Features.benefits_core)

  const { canUpsellPayroll } = useCanUpsellPayroll()

  const availableModules: ModuleConfig[] = []

  if (hasExpenses) {
    availableModules.push({
      key: 'expenses',
      titleKey: 'compensations.settings.connected_modules.expenses',
      illustration: 'expenses' as const,
      navigationPath: 'expenses-reimbursement',
    })
  }

  if (hasTimeTracking) {
    availableModules.push({
      key: 'timeTracking',
      titleKey: 'compensations.settings.connected_modules.time_tracking',
      illustration: 'timer' as const,
      navigationPath: 'time-reimbursement',
    })
  }

  if (hasBenefits) {
    availableModules.push({
      key: 'benefits',
      titleKey: 'compensations.settings.connected_modules.benefits.title',
      illustration: 'benefits' as const,
      navigationPath: 'benefits',
    })
  }

  const hasConnectedModules = availableModules.length > 0

  useUnsafeEffect(() => {
    setPrimoboxEnabled(hasFeature('integrations_primobox_active'))
  }, [hasFeature, features])

  const hasA3innuvaInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.a3innuva
  )
  const hasA3nomInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.a3nom
  )
  const hasDatevInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.datev
  )
  const hasDatevApiInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.datev_api
  )
  const hasDatevLugApiInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.datev_lug_api
  )
  const hasZucchettiInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.zucchetti
  )
  const hasPaierhInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.paierh
  )
  const hasYeapPaierhInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.yeap_paierh
  )
  const hasSilaeInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.silae
  )
  const hasGispagheInstalled = companyInstallationsData.find(
    (installation) => installation.integrationDatoId === integrationCodes.gispaghe
  )
  const hasIntegrationInstalled =
    hasA3innuvaInstalled ||
    hasA3nomInstalled ||
    hasDatevInstalled ||
    hasZucchettiInstalled ||
    hasPaierhInstalled ||
    hasYeapPaierhInstalled ||
    hasSilaeInstalled ||
    hasDatevApiInstalled ||
    hasDatevLugApiInstalled ||
    hasGispagheInstalled

  const allSettingsContent = (
    <>
      {hasCompensations && <CompensationSettings compensationSettings={compensationSettings} />}
      {!canUpsellPayroll && hasFeature('factorial_professionals') && <ProfessionalSettings />}
      {<EmployeeUpdates canUpsellPayroll={canUpsellPayroll} />}
      {hasIntegrationInstalled && <Integrations installedIntegrations={companyInstallationsData} />}
      {hasConnectedModules && (
        <ConnectedModules modules={availableModules} canUpsellPayroll={canUpsellPayroll} />
      )}
    </>
  )

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false}>
      {hasFeature('dev_compensations_cycles_management') && <Cycles />}
      {canEditPayrollConcepts && <PayrollConceptsCard />}
      {allSettingsContent}
      {hasFeature('integrations_primobox_activable') && (
        <PrimoboxActivationRow enabled={isPrimoboxEnabled} onToggle={setPrimoboxEnabled} />
      )}
    </ResponsivePage.Body>
  )
}

export default CompensationsSettingsPage
